"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZONES = exports.REGIONS = exports.resolveLocationSchema = exports.districtAliasSchema = exports.districtAreaSchema = exports.districtSchema = void 0;
exports.normalizeDistrictName = normalizeDistrictName;
exports.addressCandidates = addressCandidates;
exports.regionsInText = regionsInText;
exports.pickDistrict = pickDistrict;
exports.splitSpellings = splitSpellings;
// District spellings (plan 4.1: "all spellings (EN/AR/typos) merged").
// Every spelling is stored in its normalized form; matching compares normalized text.
const zod_1 = require("zod");
const ARABIC_DIACRITICS = /[ً-ْٰـ]/g; // harakat, dagger alef, tatweel
/**
 * Normalizes a district name or address fragment for matching:
 * lower case, no accents/diacritics, unified Arabic letter forms, no "al"/"ال"
 * prefix, punctuation removed, single spaces.
 *   "Al-Muraqqabat" → "muraqqabat"   "المُرَقَّبات" → "مرقبات"   "AL QUOZ 4" → "quoz 4"
 */
function normalizeDistrictName(input) {
    let s = input.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
    s = s
        .replace(ARABIC_DIACRITICS, '')
        .replace(/[أإآٱ]/g, 'ا')
        .replace(/ة/g, 'ه')
        .replace(/ى/g, 'ي')
        .replace(/ؤ/g, 'و')
        .replace(/ئ/g, 'ي')
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
    s = s.replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    // drop the article at the start of each word: "al ", "el ", "ال"
    s = s
        .split(' ')
        .filter((w) => w !== 'al' && w !== 'el')
        .map((w) => (w.startsWith('ال') && w.length > 3 ? w.slice(2) : w))
        .join(' ');
    return s.replace(/\s+/g, ' ').trim();
}
/**
 * Candidate pieces of a free-text address to look up as district spellings:
 * each comma/slash/dash-separated part, then word runs inside it (longest first).
 */
function addressCandidates(address, maxWords = 4) {
    const found = [];
    const seen = new Set();
    for (const part of address.split(/[,/|;\n]+|\s-\s/)) {
        const words = normalizeDistrictName(part).split(' ').filter(Boolean);
        for (let size = Math.min(maxWords, words.length); size >= 1; size--) {
            for (let i = 0; i + size <= words.length; i++) {
                const chunk = words.slice(i, i + size).join(' ');
                if (chunk.length >= 3 && !/^\d+$/.test(chunk) && !seen.has(chunk)) {
                    seen.add(chunk);
                    found.push({ chunk, size });
                }
            }
        }
    }
    // longest pieces first across the whole address, so "dubai marina" beats "marina"
    return found.sort((a, b) => b.size - a.size).map((f) => f.chunk);
}
const code = zod_1.z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_-]{1,31}$/, 'CODE_FORMAT');
exports.districtSchema = zod_1.z.object({
    code,
    name: zod_1.z.string().trim().min(2).max(120),
    hubId: zod_1.z.uuid().nullish(),
    region: zod_1.z.enum(['Dubai', 'Abu Dhabi', 'Al Ain', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah']).nullish(),
    centreLat: zod_1.z.number().min(22).max(27).nullish(),
    centreLng: zod_1.z.number().min(51).max(57).nullish(),
    zone: zod_1.z.enum(['City', 'Outer', 'Remote']).nullish(),
    isActive: zod_1.z.boolean().default(true),
    reason: zod_1.z.string().trim().max(500).optional(),
});
const position = zod_1.z.tuple([zod_1.z.number().min(-180).max(180), zod_1.z.number().min(-90).max(90)]);
const ring = zod_1.z.array(position).min(4);
/** GeoJSON Polygon or MultiPolygon (from the map drawing or an uploaded file). */
exports.districtAreaSchema = zod_1.z.object({
    geometry: zod_1.z.discriminatedUnion('type', [
        zod_1.z.object({ type: zod_1.z.literal('Polygon'), coordinates: zod_1.z.array(ring).min(1) }),
        zod_1.z.object({ type: zod_1.z.literal('MultiPolygon'), coordinates: zod_1.z.array(zod_1.z.array(ring).min(1)).min(1) }),
    ]),
    reason: zod_1.z.string().trim().max(500).optional(),
});
exports.districtAliasSchema = zod_1.z.object({
    alias: zod_1.z.string().trim().min(2).max(120),
});
exports.resolveLocationSchema = zod_1.z
    .object({
    text: zod_1.z.string().trim().max(500).optional(),
    latitude: zod_1.z.coerce.number().min(-90).max(90).optional(),
    longitude: zod_1.z.coerce.number().min(-180).max(180).optional(),
})
    .refine((v) => v.text || (v.latitude != null && v.longitude != null), { message: 'TEXT_OR_POINT_REQUIRED' });
// ---------------------------------------------------------------- regions (same district names in several emirates)
/** The seven emirates plus the Al Ain region (its own hub). A district belongs to one of them. */
exports.REGIONS = ['Dubai', 'Abu Dhabi', 'Al Ain', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];
/** City / Outer / Remote, as Operations gives it (used to size the automatic district areas). */
exports.ZONES = ['City', 'Outer', 'Remote'];
/** Words (normalized, see normalizeDistrictName) that name each region in an address or a city column. */
const REGION_WORDS = {
    Dubai: ['dubai', 'dxb', 'دبي'],
    'Abu Dhabi': ['abu dhabi', 'abudhabi', 'abu dabi', 'auh', 'ابو ظبي', 'ابوظبي'],
    'Al Ain': ['ain', 'ain city', 'عين'],
    Sharjah: ['sharjah', 'shj', 'khor fakkan', 'khorfakkan', 'kalba', 'شارقه', 'خورفكان', 'كلباء'],
    Ajman: ['ajman', 'عجمان'],
    'Umm Al Quwain': ['umm quwain', 'umm quwwain', 'uaq', 'ام قيوين'],
    'Ras Al Khaimah': ['ras khaimah', 'rak', 'راس خيمه'],
    Fujairah: ['fujairah', 'fujeirah', 'فجيره'],
};
/** Regions named in a piece of text ("Al Nahda, Sharjah" → Sharjah). "Abu Dhabi" also covers Al Ain (same emirate). */
function regionsInText(...texts) {
    const found = new Set();
    for (const text of texts) {
        if (!text)
            continue;
        const padded = ` ${normalizeDistrictName(text)} `;
        for (const region of exports.REGIONS) {
            if (REGION_WORDS[region].some((w) => padded.includes(` ${w} `)))
                found.add(region);
        }
    }
    if (found.has('Abu Dhabi'))
        found.add('Al Ain');
    return found;
}
/**
 * One spelling can name districts in several regions (Al Nahda: Dubai, Sharjah, Abu Dhabi).
 * Keeps the one in a region the address names, else the one served by the batch's hub;
 * returns null when it is still not clear (a person chooses in Addresses to fix).
 */
function pickDistrict(candidates, regions, hubId) {
    const unique = [...new Map(candidates.map((c) => [c.id, c])).values()];
    if (unique.length <= 1)
        return unique[0] ?? null;
    const inRegion = unique.filter((c) => c.region && regions.has(c.region));
    if (inRegion.length === 1)
        return inRegion[0];
    const pool = inRegion.length ? inRegion : unique;
    const atHub = hubId ? pool.filter((c) => c.hubId === hubId) : [];
    return atHub.length === 1 ? atHub[0] : null;
}
/** A spellings cell may hold several, separated by commas, Arabic commas, semicolons or new lines. */
function splitSpellings(cell) {
    return [...new Set(cell.split(/[,،;\n]+/).map((s) => s.trim()).filter(Boolean))];
}
//# sourceMappingURL=geo.js.map